import {AccentStyle, DefaultTheme} from "styled-components";
import {ThemeProps} from "styled-components/index";
import {ComponentType, createContext} from "preact";
import {useContext} from "preact/hooks";
import {forwardRef} from "react";
import hoistNonReactStatics from "../lib/hoist";

export type AccentType = "default" | "muted" | "primary" | "danger" | "info" | "warning" | "success";

export type Accents = {
    accent?: AccentType | undefined;
};

export const getAccentStyle = (props: ThemeProps<DefaultTheme> & Accents): AccentStyle => {
    const contextAccent = useAccent();
    const currentAccent = props.accent ?? contextAccent;

    return Object.assign(
        {},
        props.theme.colors,
        props.theme.accents["default"],
        currentAccent ? props.theme.accents[currentAccent] : undefined
    );
}

const AccentContext = createContext<AccentType | null>(null);

export function useAccent(): AccentType | null {
    return useContext(AccentContext);
}

export function withAccent<P extends object = any>(Component: ComponentType<P>, defaultAccent: AccentType | null = null) {
    const WithAccent = forwardRef<ComponentType<P & Accents>, P & Accents>(
        ({accent: compAccent, ...props}, forwardedRef) => {
            const accent = useAccent();
            const derivedAccent = compAccent ?? accent ?? defaultAccent ?? null;
            return <AccentContext.Provider value={derivedAccent}>{
                <Component {...props} ref={forwardedRef} />
            }</AccentContext.Provider>;
        }
    );

    return hoistNonReactStatics(WithAccent, Component);
}
